import type { ReactNode } from 'react'

import {
  ChartColumnIcon,
  EyeIcon,
  FileTextIcon,
  TrendingUpIcon,
  ZapIcon,
} from '@buffer-mono/popcorn'

export type NarrativeViewKey =
  | 'posts'
  | 'content-impact'
  | 'audience-growth'
  | 'visibility'
  | 'follower-growth-rate'

export interface NarrativeView {
  key: NarrativeViewKey
  title: string
  /**
   * Bold title shown in the metric info HoverCard. Omitted for views that have
   * no info card (e.g. Posts, which opens a dropdown instead).
   */
  infoTitle?: string
  /**
   * Description shown under `infoTitle` in the metric info HoverCard, and
   * surfaced as the chip's accessible description for screen-reader users.
   */
  description: string
  icon: ReactNode
  /** Metric keys for this view, in order. Order determines which metric gets
   * the primary (left) Y-axis and the first slot in the summary panel. */
  metricKeys: string[]
}

export const NARRATIVE_VIEWS: NarrativeView[] = [
  {
    key: 'posts',
    title: 'Posts',
    description: 'Posts published over the selected period.',
    icon: <FileTextIcon size="small" />,
    metricKeys: ['posts'],
  },
  {
    key: 'content-impact',
    title: 'Content Impact',
    infoTitle: 'Posts and Followers',
    description:
      'Shows whether your posting activity is growing your audience.',
    icon: <ZapIcon size="small" />,
    metricKeys: ['posts', 'followers'],
  },
  {
    key: 'audience-growth',
    title: 'Audience Growth',
    infoTitle: 'Followers Over Time',
    description: 'Shows whether your audience is growing.',
    icon: <TrendingUpIcon size="small" />,
    metricKeys: ['followers', 'followers-net-new'],
  },
  {
    key: 'visibility',
    title: 'Visibility',
    infoTitle: 'Profile Views and Followers',
    description:
      'Shows how many people who discover your profile actually follow you.',
    icon: <EyeIcon size="small" />,
    metricKeys: ['views', 'followers'],
  },
  {
    key: 'follower-growth-rate',
    title: 'Follower Growth Rate',
    infoTitle: 'Growth Rate and Buffer Median',
    description: 'Shows where you stand compared to similar accounts.',
    icon: <ChartColumnIcon size="small" />,
    // benchmark-growth-rate = this channel's monthly rate
    // benchmark-median      = p50 of the comparable cohort
    metricKeys: ['benchmark-growth-rate', 'benchmark-median'],
  },
]

export function getDefaultNarrativeViewKey(
  hasFollowers: boolean,
): NarrativeViewKey {
  return hasFollowers ? 'content-impact' : 'posts'
}
